import { useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from '@remix-run/react';
import {
	Grid,
	Card,
	CardContent,
	IconButton,
	useMediaQuery,
	Divider,
	Button,
	Typography,
	Box,
	List,
	Avatar,
	ImageList,
	ImageListItem,
	ListItem,
	ListItemText,
	ListItemAvatar,
	CardActions,
	ToggleButtonGroup,
	ToggleButton,
	Skeleton,
	TextField,
	Checkbox,
	CircularProgress,
	FormHelperText,
	FormHelperTextProps,
	FormGroup,
	FormControlLabel,
	FormControl,
	OutlinedInput,
	InputAdornment,
	LinearProgress,
	MenuItem,
} from '@mui/material';
import { ImageLoader } from '~/content/components/ImageLoader';
import leon from '~/content/images/index_page/leon_bernard_900.webp';

// import PhonelinkRingSharpIcon from '@mui/icons-material/PhonelinkRingSharp';

import {
	PhonelinkRingSharp as PhonelinkRingSharpIcon,
	ForwardToInboxSharp as ForwardToInboxSharpIcon,
} from '@mui/icons-material';

// vorbild
// https://www2.deloitte.com/de/de/footerlinks/submit-rfp.html?icid=bottom_submit-rfp

const Headline = (): JSX.Element => {
	let { t } = useTranslation(['hireUs']);
	const theme = useTheme();
	const isXs = useMediaQuery(theme.breakpoints.up('xs'), {
		defaultMatches: true,
	});
	const isSm = useMediaQuery(theme.breakpoints.up('sm'), {
		defaultMatches: true,
	});
	const isMd = useMediaQuery(theme.breakpoints.up('md'), {
		defaultMatches: true,
	});
	const isLg = useMediaQuery(theme.breakpoints.up('lg'), {
		defaultMatches: true,
	});

	const leonRef_1 = useRef(null);

	return (
		<Box>
			<Box marginBottom={0}>
				{isMd ? (
					<Typography
						component={'h1'}
						variant='h3'
						align={'center'}
						sx={{
							fontWeight: 700,
						}}
						display={{ xs: 'none', md: 'block' }}
					>
						{t('headline_1', { hyphen: '\u00AD' })}
					</Typography>
				) : (
					<Typography
						component={'h1'}
						variant='h3'
						align={'center'}
						sx={{
							fontWeight: 700,
						}}
						display={{ xs: 'block', md: 'none' }}
					>
						{t('headline_2')}
					</Typography>
				)}
			</Box>

			<Grid
				container
				direction='row'
				justifyContent='center'
				alignItems='flex-end'
				sx={{
					position: 'relative',
				}}
			>
				<Grid
					item
					xs
					sm={5}
					container
					direction='column'
					justifyContent='center'
					alignItems='center'
					sx={{
						position: 'relative',
						top: isSm ? '15px' : '-30px',
						// paddingBottom: isSmDown ? '40px' : '0px',
					}}
				>
					<Box>
						<ImageLoader
							intersectViewportCheck={true}
							childRef={leonRef_1}
							style={{
								overflow: 'hidden',
								display: 'flex',
								justifyContent: 'center',
							}}
						>
							<Box
								ref={leonRef_1}
								component={'img'}
								height={1}
								width={1}
								minWidth={'200px'}
								maxWidth={{ xs: '260px', md: '315px' }}
								minHeight={200}
								src={leon}
								paddingLeft={{ xs: '17px', md: '0px' }}
								alt='Person Leon Bernard Ansprechpartner für Software Angebote Anfragen'
								sx={{
									filter: theme.palette.mode === 'light' ? 'brightness(1.10)' : 'brightness(1.03)',
									objectFit: 'cover',
									position: 'relative',
									top: '65px',
								}}
							/>
						</ImageLoader>
						<Divider />
					</Box>

					<Box>
						<Typography
							variant={'body2'}
							component={'h4'}
							color='text.primary'
							align={isSm ? 'left' : 'center'}
							sx={{
								lineHeight: isMd ? 1.4 : 1.3,
								fontWeight: 700,
							}}
							pt={1}
						>
							{t('askExpertInfo1')}
						</Typography>
						<Typography
							align={isSm ? 'left' : 'center'}
							variant={'body2'}
							component={'h4'}
							color={'text.secondary'}
							sx={{
								lineHeight: isMd ? 1.4 : 1.3,
							}}
						>
							{t('askExpertInfo2')}
						</Typography>
					</Box>
				</Grid>

				<Grid item xs sm={5}>
					<Box marginBottom={{ sx: 5, sm: 0, md: 3 }} marginTop={0}>
						{/* desktop */}
						<Typography
							mt={2}
							mb={1}
							variant='subtitle1'
							// align={isSmDown ? 'center' : 'left'}
							align={isSm ? 'left' : 'center'}
							color='text.primary'
							display={{ xs: 'none', md: 'block' }}
							sx={{
								fontWeight: 700,
							}}
						>
							{t('subHeadline_1')}
							{/* {t('subHeadline_2')} */}
						</Typography>

						{/* mobil */}
						<Typography
							pt={{ xs: 0, sm: 10, md: 0 }}
							pb={{ xs: 1.5, sm: 0.5, md: 1.5 }}
							variant='body1'
							// align={isSmDown ? 'center' : 'left'}
							align={isSm ? 'left' : 'center'}
							color='text.primary'
							display={{ xs: 'block', md: 'none' }}
							sx={{
								fontWeight: 700,
							}}
						>
							{t('subHeadline_1')}
							{/* <br></br>
							{t('subHeadline_2')} */}
						</Typography>

						<Typography
							component={'p'}
							variant='body1'
							color={'text.secondary'}
							align={isSm ? 'left' : 'center'}
							mb={2}
						>
							{t('infoText_1')}
							<Typography
								// component={'p'}
								component={'span'}
								variant={'inherit'}
								// variant='body1'
								color={'text.primary'}
								fontWeight={700}
								align={isSm ? 'left' : 'center'}
								// mb={2}
							>
								{' '}
								{t('infoText_1_2')}
							</Typography>{' '}
							{t('infoText_1_3')}
						</Typography>

						{/* <Typography
							component={'p'}
							variant='body1'
							color={'text.secondary'}
							align={isSm ? 'left' : 'center'}
							mb={2}
						>
							{t('infoText_1_3')}
						</Typography> */}

						<Typography
							mb={0.6}
							component={'p'}
							variant='body1'
							color={'text.secondary'}
							align={isSm ? 'left' : 'center'}
						>
							{t('infoText_2')}
						</Typography>

						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: isSm ? 'start' : 'center',
								justifyContent: isSm ? 'left' : 'center',
							}}
						>
							<Typography
								width='fit-content'
								mb={0.2}
								color='text.primary'
								component={'a'}
								variant='body1'
								// style={{ textDecoration: 'none' }}
								fontWeight={700}
								href={'mailto: anfrage@northrock.software'}
								target='_blank'
								align={isSm ? 'left' : 'center'}
								display={'block'}
								whiteSpace='nowrap'
							>
								{' '}
								<Box
									component={'span'}
									sx={{
										position: 'relative',
										width: '24px',
										display: 'inline-block',
									}}
								>
									<ForwardToInboxSharpIcon
										sx={{
											fontSize: '16px',
											position: 'absolute',
											bottom: '-3px',
											right: '7px',
											color: 'text.primary',
										}}
									/>
								</Box>
								{/* E-Mail schreiben */}
								{/* l.bernard@northrock.software */}
								anfrage@northrock.software
							</Typography>

							{/* {t('infoText_3')} */}
							<Typography
								width='fit-content'
								color='text.primary'
								component={'a'}
								variant='body1'
								fontWeight={700}
								style={{ textDecoration: 'none' }}
								href={'tel: +491731719939'}
								target='_blank'
								display={'block'}
							>
								<Box
									component={'span'}
									sx={{
										position: 'relative',
										width: '24px',
										display: 'inline-block',
									}}
								>
									<PhonelinkRingSharpIcon
										sx={{
											fontSize: '14px',
											position: 'absolute',
											bottom: '-1px',
											right: '7px',
											color: 'text.primary',
										}}
									/>
								</Box>
								+49 173 1719939
								{/* Jetzt anrufen */}
							</Typography>
						</Box>

						{/* . */}
					</Box>
				</Grid>
			</Grid>
		</Box>
	);
};

export default Headline;
